
import React from 'react';
import { useAuth } from '@/src/contexts/AuthContext';

interface HeaderProps {
  setIsUpgradeModalOpen: (open: boolean) => void;
  setIsProfileMenuOpen: (open: boolean) => void;
  onLogoClick?: () => void;
  onOpenSavedOpportunities?: () => void;
  savedCount?: number;
}

const Header: React.FC<HeaderProps> = ({
  setIsUpgradeModalOpen,
  setIsProfileMenuOpen,
  onLogoClick,
  onOpenSavedOpportunities,
  savedCount = 0
}) => {
  const { user, userProfile, isLoggedIn, userType } = useAuth();
  const { setIsSignInModalOpen, setIsSignUpModalOpen } = useAuth();

  return (
    <header className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between sticky top-0 bg-[#322D29] z-[200]">
      <button
        onClick={(e) => {
          e.preventDefault();
          if (onLogoClick) onLogoClick();
        }}
        className="flex items-center gap-3.5 text-left focus:outline-none cursor-pointer group select-none"
      >
        <div className="w-10 h-10 bg-[#3a3430] border border-white/[0.06] rounded-lg flex items-center justify-center text-[#FFFFFF] transition-colors duration-200 group-hover:border-[#F26522]/40">
          <i className="fas fa-satellite-dish text-sm transition-colors duration-200"></i>
        </div>
        <div>
          <h1 className="text-xl md:text-2xl font-bold tracking-tight font-heading leading-none text-[#FFFFFF]">
            eNvesta <span className="text-[#F26522]">AI</span>
          </h1>
          <span className="text-[10px] uppercase tracking-[0.25em] text-white/60 block mt-1 transition-colors duration-200">
            Venture Intelligence
          </span>
        </div>
      </button>
      <div className="flex items-center gap-2 sm:gap-4">
        {onOpenSavedOpportunities && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onOpenSavedOpportunities();
            }}
            id="header-access-saved-btn"
            className="relative flex items-center gap-2 px-3 py-2 rounded-lg bg-[#3a3430] border border-white/[0.08] hover:border-[#F26522]/50 text-white/80 hover:text-white text-xs font-medium transition-all cursor-pointer group"
            title="Access Saved Opportunities"
          >
            <i className="fas fa-bookmark text-xs text-[#F26522] transition-transform group-hover:scale-110"></i>
            <span className="hidden sm:inline font-medium">Saved Opportunities</span>
            {savedCount > 0 && (
              <span className="bg-[#F26522] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none flex items-center justify-center min-w-[18px]">
                {savedCount}
              </span>
            )}
          </button>
        )}
        {!isLoggedIn ? (
          <>
            <button 
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsSignInModalOpen(true); }}
              className="text-xs font-medium text-white/70 hover:text-white px-4 py-2 rounded-lg transition-colors cursor-pointer"
            >
              Log In
            </button>
            <button 
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsSignUpModalOpen(true); }}
              className="bg-[#F26522] hover:bg-[#F26522]/90 text-[#FFFFFF] text-xs font-medium px-4 py-2 rounded-lg transition-colors cursor-pointer"
            >
              Sign Up
            </button>
          </>
        ) : (
          <div className="relative">
            <button 
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsProfileMenuOpen(true); }}
              className="flex items-center gap-3 px-3 py-1.5 rounded-lg hover:bg-[#3a3430] transition-all border border-transparent hover:border-white/[0.06] active:scale-95 group cursor-pointer"
            >
              <div className="flex flex-col items-end">
                <span className="text-[11px] font-medium text-[#FFFFFF] leading-none">
                  {userProfile?.name || user?.displayName || (userType === 'company' ? 'Enterprise User' : 'Individual User')}
                </span>
                <span className="text-[9px] uppercase tracking-[0.15em] text-[#F26522] mt-1">
                  {userType === 'company' 
                    ? 'ENTERPRISE ACCOUNT' 
                    : 'INDIVIDUAL ACCOUNT'}
                </span>
              </div>
              <div className="w-9 h-9 rounded-full bg-[#3a3430] border border-white/[0.06] overflow-hidden flex items-center justify-center group-hover:border-[#F26522]/40 transition-all">
                {userProfile?.photoURL || user?.photoURL ? (
                  <img src={userProfile?.photoURL || user?.photoURL} alt="Profile" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <i className="fas fa-user-secret text-white/50 text-xs"></i>
                )}
              </div>
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;

